import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

const IVC_LEVEL = {
    3: { tag: 'Niv.3', label: 'RIESGO CRITICO', color: '#E74C3C', text: '#fff' },
    2: { tag: 'Niv.2', label: 'RIESGO ALTO', color: '#E67E22', text: '#fff' },
    1: { tag: 'Niv.1', label: 'RIESGO MODERADO', color: '#D4AC0D', text: '#1a1a1a' },
    0: { tag: 'Sin IVC', label: 'RIESGO BAJO', color: '#62C190', text: '#fff' },
};

const getLeadershipState = (score) => {
    const s = Number(score);
    if (!Number.isFinite(s)) return { label: '-', color: '#E8EEF5', text: '#617993' };
    if (s < 65) return { label: 'RIESGO CRITICO', color: '#E74C3C', text: '#fff' };
    if (s < 75) return { label: 'RIESGO MODERADO', color: '#D4AC0D', text: '#1a1a1a' };
    return { label: 'RIESGO BAJO', color: '#62C190', text: '#fff' };
};

const getSelfcareState = (score) => {
    const s = Number(score);
    if (!Number.isFinite(s)) return { label: '-', color: '#E8EEF5', text: '#617993' };
    if (s < 60) return { label: 'RIESGO CRITICO', color: '#E74C3C', text: '#fff' };
    if (s < 65) return { label: 'RIESGO ALTO', color: '#E67E22', text: '#fff' };
    if (s < 80) return { label: 'RIESGO MODERADO', color: '#D4AC0D', text: '#1a1a1a' };
    return { label: 'RIESGO BAJO', color: '#62C190', text: '#fff' };
};

const getNpsState = (nps) => {
    const v = Number(nps);
    if (!Number.isFinite(v)) return { label: '-', color: '#E8EEF5', text: '#617993' };
    if (v < 0) return { label: 'DETRACTOR', color: '#E74C3C', text: '#fff' };
    if (v < 30) return { label: 'PASIVO', color: '#D4AC0D', text: '#1a1a1a' };
    return { label: 'PROMOTOR', color: '#62C190', text: '#fff' };
};

const getBarColor = (score) => {
    const s = Number(score);
    if (!Number.isFinite(s)) return '#B9C7D6';
    if (s >= 80) return '#E74C3C';
    if (s >= 60) return '#E67E22';
    if (s >= 50) return '#D4AC0D';
    return '#62C190';
};

const ManCrossPageRiskMatrix = ({ reportData, headerText, pageNumber }) => {
    const payload = reportData?.payload || {};
    const scoreByArea = payload?.score_risk_by_area || {};
    const ivcByArea = payload?.ivc_by_area || {};

    const ipraMap = useMemo(() => {
        const map = {};
        (payload?.totals?.ipra?.average_by_management_area || []).forEach((e) => {
            map[e.name] = Number(e.average);
        });
        return map;
    }, [payload]);

    const iashMap = useMemo(() => {
        const map = {};
        (payload?.totals?.iash?.average_by_management_area || []).forEach((e) => {
            map[e.name] = Number(e.average);
        });
        return map;
    }, [payload]);

    const npsMap = useMemo(() => {
        const map = {};
        (payload?.nps_data?.by_area || []).forEach((e) => {
            map[e.area] = Number(e.nps);
        });
        return map;
    }, [payload]);

    const rows = useMemo(() => {
        const allNames = new Set([
            ...Object.keys(scoreByArea),
            ...Object.keys(ipraMap),
            ...Object.keys(iashMap),
            ...Object.keys(npsMap),
            ...Object.keys(ivcByArea),
        ]);

        return [...allNames]
            .map((name) => {
                const normalized = Number(scoreByArea?.[name]?.normalized ?? 0);
                const ivcNivel = Number(ivcByArea?.[name]?.nivel ?? scoreByArea?.[name]?.nivel ?? 0);
                return {
                    name,
                    ipra: ipraMap[name],
                    iash: iashMap[name],
                    nps: npsMap[name],
                    ivcNivel,
                    normalized,
                };
            })
            .sort((a, b) => b.normalized - a.normalized);
    }, [scoreByArea, ipraMap, iashMap, npsMap, ivcByArea]);

    const topArea = rows[0] || null;
    const secondArea = rows[1] || null;
    const ratio = secondArea && secondArea.normalized > 0
        ? topArea.normalized / secondArea.normalized
        : 0;

    const summaryText = topArea
        ? `El ${Math.round(topArea.normalized)}% del riesgo se concentra en un solo cuadrante - ${topArea.name} en emergencia sistémica.`
        : 'Sin datos suficientes para estimar concentración de riesgo por área.';

    const footerText = topArea && secondArea
        ? `${topArea.name} (score ${Math.round(topArea.normalized)}/100) es ${ratio.toFixed(1)}x más riesgosa que la segunda área. No necesita monitoreo, necesita intervención inmediata.`
        : 'No hay suficientes áreas para calcular contraste de riesgo.';

    return (
        <div className="mgcr-matrix-page">
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Matriz Crítica</h2>
                <p className="mgcr-dx-subtitle">Concentración de Riesgo</p>
            </div>

            <div className="mgcr-matrix-board">
                <div className="mgcr-matrix-left">
                    <div className="mgcr-matrix-callout-row">
                        <span className="mgcr-matrix-callout-title">Matriz de Estados</span>
                        <span className="mgcr-matrix-callout-divider" />
                        <span className="mgcr-matrix-callout-text">{summaryText}</span>
                    </div>

                    <div className="mgcr-matrix-state-table">
                        <div className="mgcr-matrix-state-head">
                            <span>Área</span>
                            <span>Liderazgo <em>(IPRA)</em></span>
                            <span>Autocuidado <em>(IASH)</em></span>
                            <span>Confianza <em>(NPS)</em></span>
                            <span>Co-falla <em>(IVC)</em></span>
                        </div>

                        {rows.map((row) => {
                            const ipra = getLeadershipState(row.ipra);
                            const iash = getSelfcareState(row.iash);
                            const nps = getNpsState(row.nps);
                            const ivc = IVC_LEVEL[row.ivcNivel] || IVC_LEVEL[0];
                            return (
                                <div key={row.name} className="mgcr-matrix-state-row">
                                    <span className="mgcr-matrix-area">{row.name}</span>
                                    <span className="mgcr-matrix-badge" style={{ background: `${ipra.color}22`, color: ipra.color }}>
                                        {ipra.label}
                                    </span>
                                    <span className="mgcr-matrix-badge" style={{ background: `${iash.color}22`, color: iash.color }}>
                                        {iash.label}
                                    </span>
                                    <span className="mgcr-matrix-badge" style={{ background: `${nps.color}22`, color: nps.color }}>
                                        {nps.label}
                                    </span>
                                    <span className="mgcr-matrix-ivc-pill" style={{ background: ivc.color, color: ivc.text }}>
                                        {ivc.tag}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="mgcr-matrix-right">
                    {rows.map((row) => {
                        const value = Math.max(2, Math.round(row.normalized || 0));
                        const barColor = getBarColor(row.normalized);
                        return (
                            <div key={`bar-${row.name}`} className="mgcr-matrix-rank-row">
                                <div className="mgcr-matrix-rank-label">{row.name}</div>
                                <div className="mgcr-matrix-rank-track">
                                    <div className="mgcr-matrix-rank-fill" style={{ width: `${value}%`, background: barColor }} />
                                </div>
                                <div className="mgcr-matrix-rank-score" style={{ color: barColor }}>{value}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mgcr-insight-footer">
                <strong>Esto implica que:</strong> {footerText}
            </div>

            <div className="keep-blankspace" />

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageRiskMatrix;
